import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]",
        cta: "bg-gradient-cta text-cta-foreground hover:opacity-90 shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98] glow-cta font-bold",
        "cta-gradient":
          "bg-gradient-primary text-white shadow-xl hover:shadow-2xl hover:scale-[1.03] active:scale-[0.98] font-bold glow-primary",
        accent:
          "bg-gradient-accent text-accent-foreground hover:opacity-90 shadow-md hover:shadow-lg",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md",
        success:
          "bg-success text-success-foreground hover:bg-success/90 shadow-md",
        outline:
          "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground",
        "outline-cta":
          "border-2 border-cta bg-transparent text-cta hover:bg-cta hover:text-white",
        ghost: "hover:bg-accent/10 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        glass:
          "glass-effect text-foreground hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md",
        "glass-primary":
          "bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-md border border-white/20 text-foreground hover:from-primary/30 hover:to-accent/30 shadow-lg",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
